import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../redux/Action';
import { toast } from 'react-toastify';
import axios from '../../redux/axiosInstance';
import './ManageApprovers.css';

const ManageApprovers = () => {
  const apiUrl = process.env.REACT_APP_API;
  const dispatch = useDispatch();
  const { users: allUsers, error } = useSelector((state) => state.getUsers);
  
  const [selectedLevel1, setSelectedLevel1] = useState([]);
  const [selectedLevel2, setSelectedLevel2] = useState([]);
  const [selectedLevel3, setSelectedLevel3] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getUsers());
    fetchApprovers();
  }, [dispatch]);

  const fetchApprovers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/get-approvers`);
      if (response.data) {
        setSelectedLevel1(response.data.level1 || []);
        setSelectedLevel2(response.data.level2 || []);
        setSelectedLevel3(response.data.level3 || []);
      }
    } catch (error) {
      console.error('Error fetching approvers:', error);
      // If no approvers exist yet, start with empty arrays
      setSelectedLevel1([]);
      setSelectedLevel2([]);
      setSelectedLevel3([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLevel1Change = (e) => {
    const selectedEmail = e.target.value;
    if (!selectedEmail) return;
    
    const user = allUsers.find(u => u.email === selectedEmail);
    if (user && !selectedLevel1.find(u => u.email === user.email)) {
      setSelectedLevel1([...selectedLevel1, { name: user.name, email: user.email }]);
    }
    e.target.value = ''; // Reset dropdown
  };

  const handleLevel2Change = (e) => {
    const selectedEmail = e.target.value;
    if (!selectedEmail) return;
    
    const user = allUsers.find(u => u.email === selectedEmail);
    if (user && !selectedLevel2.find(u => u.email === user.email)) {
      setSelectedLevel2([...selectedLevel2, { name: user.name, email: user.email }]);
    }
    e.target.value = ''; // Reset dropdown
  };

  const handleLevel3Change = (e) => {
    const selectedEmail = e.target.value;
    if (!selectedEmail) return;
    
    const user = allUsers.find(u => u.email === selectedEmail);
    if (user && !selectedLevel3.find(u => u.email === user.email)) {
      setSelectedLevel3([...selectedLevel3, { name: user.name, email: user.email }]);
    }
    e.target.value = ''; // Reset dropdown
  };

  const handleCheckboxChange = (level, email, checked) => {
    if (level === 1) {
      if (checked) {
        const user = allUsers.find(u => u.email === email);
        if (user && !selectedLevel1.find(u => u.email === user.email)) {
          setSelectedLevel1([...selectedLevel1, { name: user.name, email: user.email }]);
        }
      } else {
        setSelectedLevel1(selectedLevel1.filter(u => u.email !== email));
      }
    } else if (level === 2) {
      if (checked) {
        const user = allUsers.find(u => u.email === email);
        if (user && !selectedLevel2.find(u => u.email === user.email)) {
          setSelectedLevel2([...selectedLevel2, { name: user.name, email: user.email }]);
        }
      } else {
        setSelectedLevel2(selectedLevel2.filter(u => u.email !== email));
      }
    } else if (level === 3) {
      if (checked) {
        const user = allUsers.find(u => u.email === email);
        if (user && !selectedLevel3.find(u => u.email === user.email)) {
          setSelectedLevel3([...selectedLevel3, { name: user.name, email: user.email }]);
        }
      } else {
        setSelectedLevel3(selectedLevel3.filter(u => u.email !== email));
      }
    }
  };

  const handleRemoveApprover = (level, email) => {
    if (level === 1) {
      setSelectedLevel1(selectedLevel1.filter(u => u.email !== email));
    } else if (level === 2) {
      setSelectedLevel2(selectedLevel2.filter(u => u.email !== email));
    } else if (level === 3) {
      setSelectedLevel3(selectedLevel3.filter(u => u.email !== email));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save each level separately
      await Promise.all([
        axios.post(`${apiUrl}/save-approvers`, {
          level: 'level1',
          approvers: selectedLevel1,
        }),
        axios.post(`${apiUrl}/save-approvers`, {
          level: 'level2',
          approvers: selectedLevel2,
        }),
        axios.post(`${apiUrl}/save-approvers`, {
          level: 'level3',
          approvers: selectedLevel3,
        }),
      ]);
      
      toast.success('Approvers saved successfully!');
    } catch (error) {
      console.error('Error saving approvers:', error);
      toast.error('Failed to save approvers. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const isUserSelected = (level, email) => {
    if (level === 1) return selectedLevel1.some(u => u.email === email);
    if (level === 2) return selectedLevel2.some(u => u.email === email);
    if (level === 3) return selectedLevel3.some(u => u.email === email);
    return false;
  };

  if (loading) {
    return (
      <div className="manage-approvers-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading approvers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-approvers-container">
      <h2 className="manage-approvers-heading">Manage Approvers</h2>
      
      {error && <p className="manage-approvers-error">Error: {error}</p>}

      <div className="approvers-section">
        {/* Level-1 Approver */}
        <div className="approver-level">
          <h3 className="approver-level-title">Level-1 Approver</h3>
          <div className="approver-dropdown-section">
            <select
              className="approver-dropdown"
              onChange={handleLevel1Change}
              defaultValue=""
            >
              <option value="" disabled>Select Level-1 Approver</option>
              {allUsers && allUsers.map((user) => (
                <option key={user._id} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="approver-list">
            <h4>Selected Level-1 Approvers:</h4>
            {selectedLevel1.length > 0 ? (
              <ul className="selected-approvers-list">
                {selectedLevel1.map((approver, index) => (
                  <li key={index} className="selected-approver-item">
                    <span>{approver.name} ({approver.email})</span>
                    <button
                      className="remove-approver-btn"
                      onClick={() => handleRemoveApprover(1, approver.email)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-approvers">No approvers selected</p>
            )}
          </div>

          <div className="approver-checkboxes">
            <h4>Select Multiple Approvers:</h4>
            <div className="checkbox-list">
              {allUsers && allUsers.map((user) => (
                <label key={user._id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={isUserSelected(1, user.email)}
                    onChange={(e) => handleCheckboxChange(1, user.email, e.target.checked)}
                  />
                  <span>{user.name} ({user.email})</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Level-2 Approver */}
        <div className="approver-level">
          <h3 className="approver-level-title">Level-2 Approver</h3>
          <div className="approver-dropdown-section">
            <select
              className="approver-dropdown"
              onChange={handleLevel2Change}
              defaultValue=""
            >
              <option value="" disabled>Select Level-2 Approver</option>
              {allUsers && allUsers.map((user) => (
                <option key={user._id} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="approver-list">
            <h4>Selected Level-2 Approvers:</h4>
            {selectedLevel2.length > 0 ? (
              <ul className="selected-approvers-list">
                {selectedLevel2.map((approver, index) => (
                  <li key={index} className="selected-approver-item">
                    <span>{approver.name} ({approver.email})</span>
                    <button
                      className="remove-approver-btn"
                      onClick={() => handleRemoveApprover(2, approver.email)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-approvers">No approvers selected</p>
            )}
          </div>

          <div className="approver-checkboxes">
            <h4>Select Multiple Approvers:</h4>
            <div className="checkbox-list">
              {allUsers && allUsers.map((user) => (
                <label key={user._id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={isUserSelected(2, user.email)}
                    onChange={(e) => handleCheckboxChange(2, user.email, e.target.checked)}
                  />
                  <span>{user.name} ({user.email})</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Level-3 Approver */}
        <div className="approver-level">
          <h3 className="approver-level-title">Level-3 Approver</h3>
          <div className="approver-dropdown-section">
            <select
              className="approver-dropdown"
              onChange={handleLevel3Change}
              defaultValue=""
            >
              <option value="" disabled>Select Level-3 Approver</option>
              {allUsers && allUsers.map((user) => (
                <option key={user._id} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          <div className="approver-list">
            <h4>Selected Level-3 Approvers:</h4>
            {selectedLevel3.length > 0 ? (
              <ul className="selected-approvers-list">
                {selectedLevel3.map((approver, index) => (
                  <li key={index} className="selected-approver-item">
                    <span>{approver.name} ({approver.email})</span>
                    <button
                      className="remove-approver-btn"
                      onClick={() => handleRemoveApprover(3, approver.email)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-approvers">No approvers selected</p>
            )}
          </div>

          <div className="approver-checkboxes">
            <h4>Select Multiple Approvers:</h4>
            <div className="checkbox-list">
              {allUsers && allUsers.map((user) => (
                <label key={user._id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={isUserSelected(3, user.email)}
                    onChange={(e) => handleCheckboxChange(3, user.email, e.target.checked)}
                  />
                  <span>{user.name} ({user.email})</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <button 
          className="save-approvers-btn" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Approvers'}
        </button>
      </div>
    </div>
  );
};

export default ManageApprovers;

